/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Cell, FormControl, VFlow } from 'bold-ui'
import { ErrorField, RadioField, RadioFieldProps } from 'components/form'
import { usePecField } from 'components/form/final-form/useField'
import theme from 'config/theme'
import { CaracteristicaLesaoEnum } from 'graphql/types.generated'
import { Fragment } from 'react'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import { getTecidosMolesCaracteristicaLesao, getTipoLesaoFundamental, TipoLesaoFundamental } from '../../../model'

export interface LesaoFundamentalFieldModel {
  value: TipoLesaoFundamental
  lesaoEnvolvida?: CaracteristicaLesaoEnum
}

export interface LesaoFundamentalFieldProps {
  name: MetaPath<LesaoFundamentalFieldModel>
}

export default function LesaoFundamentalField(props: LesaoFundamentalFieldProps) {
  const { name } = props

  const {
    input: {
      value: { value },
    },
  } = useField<LesaoFundamentalFieldModel>(name.absolutePath(), { subscription: { value: true } })

  const {
    input: { onChange },
  } = usePecField({ type: 'radio', name: name.lesaoEnvolvida })

  const handleChange = () => {
    onChange(undefined)
  }

  return (
    <Fragment>
      <Cell size={6}>
        <FormControl label='Lesão fundamental'>
          <VFlow vSpacing={0.5}>
            <TipoLesaoFundamentalRadioField
              name={name.value}
              value={TipoLesaoFundamental.PLANA}
              onChange={handleChange}
            />
            <TipoLesaoFundamentalRadioField
              name={name.value}
              value={TipoLesaoFundamental.LIQUIDA}
              onChange={handleChange}
            />
            <TipoLesaoFundamentalRadioField
              name={name.value}
              value={TipoLesaoFundamental.TECIDUAL}
              onChange={handleChange}
            />
          </VFlow>
        </FormControl>
      </Cell>

      <Cell size={6}>
        {value === TipoLesaoFundamental.PLANA && (
          <FormControl label='Lesão plana ou elevada'>
            <VFlow vSpacing={0.5}>
              <LesaoFundamentalRadioField
                name={name.lesaoEnvolvida}
                value={CaracteristicaLesaoEnum.LESAO_PLANA_MACULA}
              />
              <LesaoFundamentalRadioField
                name={name.lesaoEnvolvida}
                value={CaracteristicaLesaoEnum.LESAO_PLANA_PLACA}
              />
              <LesaoFundamentalRadioField
                name={name.lesaoEnvolvida}
                value={CaracteristicaLesaoEnum.LESAO_PLANA_PAPULA}
              />
              <LesaoFundamentalRadioField
                name={name.lesaoEnvolvida}
                value={CaracteristicaLesaoEnum.LESAO_PLANA_TUMEFACAO}
              />
              <LesaoFundamentalRadioField
                name={name.lesaoEnvolvida}
                value={CaracteristicaLesaoEnum.LESAO_PLANA_PSEUDOMEMBRANA}
              />
            </VFlow>
          </FormControl>
        )}

        {value === TipoLesaoFundamental.LIQUIDA && (
          <FormControl label='Lesão com conteúdo líquido'>
            <VFlow vSpacing={0.5}>
              <LesaoFundamentalRadioField
                name={name.lesaoEnvolvida}
                value={CaracteristicaLesaoEnum.LESAO_LIQUIDA_VESICULA}
              />
              <LesaoFundamentalRadioField
                name={name.lesaoEnvolvida}
                value={CaracteristicaLesaoEnum.LESAO_LIQUIDA_PUSTULA}
              />
            </VFlow>
          </FormControl>
        )}

        {value === TipoLesaoFundamental.TECIDUAL && (
          <FormControl label='Lesão envolvenda perda tecidual'>
            <VFlow vSpacing={0.5}>
              <LesaoFundamentalRadioField
                name={name.lesaoEnvolvida}
                value={CaracteristicaLesaoEnum.LESAO_TECIDUAL_EROSAO}
              />
              <LesaoFundamentalRadioField
                name={name.lesaoEnvolvida}
                value={CaracteristicaLesaoEnum.LESAO_TECIDUAL_ULCERA}
              />
              <LesaoFundamentalRadioField
                name={name.lesaoEnvolvida}
                value={CaracteristicaLesaoEnum.LESAO_TECIDUAL_FISSURA}
              />
            </VFlow>
          </FormControl>
        )}

        <ErrorField
          name={name.lesaoEnvolvida}
          style={css`
            margin-top: 0.25rem;
          `}
        />
      </Cell>
    </Fragment>
  )
}

const CommonRadioField = (props: RadioFieldProps) => (
  <RadioField style={classes.radio} wrapperStyles={classes.wrapperStyles} clearable {...props} />
)

const LesaoFundamentalRadioField = (props: Omit<RadioFieldProps, 'label'>) => (
  <CommonRadioField label={getTecidosMolesCaracteristicaLesao(props.value).title} {...props} />
)

const TipoLesaoFundamentalRadioField = (props: Omit<RadioFieldProps, 'label'>) => (
  <CommonRadioField label={getTipoLesaoFundamental(props.value).title} {...props} />
)

const classes = {
  radio: {
    marginLeft: -0.25,
  },
  wrapperStyles: css`
    ${theme.breakpoints.down('xl')} {
      grid-gap: 0.5rem;
    }
  `,
}
